import type { StationType } from '@/config/brand';

export type { StationType };

/** ISO-8601 timestamp string. */
export type ISODateTime = string;

/**
 * Money is stored everywhere as an integer number of paise (1 rupee = 100
 * paise). Never store money as a float. Format to ₹ only for display via
 * lib/format.ts. Rates are paise-per-minute.
 */
export type Paise = number;

// ---------------------------------------------------------------------------
// Tenancy & people
// ---------------------------------------------------------------------------

export interface Club {
  id: string;
  name: string;
  shortCode: string;
  status: 'OPEN' | 'CLOSED';
  openedAt: ISODateTime | null;
  tier: 'STANDARD' | 'PREMIUM';
}

export type Role = 'MASTER_ADMIN' | 'ADMIN' | 'MANAGER' | 'STAFF';

export const ROLE_LABEL: Record<Role, string> = {
  MASTER_ADMIN: 'Master Admin',
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  STAFF: 'Staff',
};

export interface AppUser {
  id: string;
  clubId: string;
  name: string;
  email: string;
  role: Role;
  status: 'ACTIVE' | 'PENDING' | 'DISABLED';
  initials: string;
  createdAt: ISODateTime;
}

// ---------------------------------------------------------------------------
// Attendance (reception desk roster + clock-in/out log)
// ---------------------------------------------------------------------------

/** A member of the reception desk roster whose shifts are tracked. */
export interface Receptionist {
  id: string;
  clubId: string;
  name: string;
  active: boolean;
  createdAt: ISODateTime;
}

/**
 * One shift: a clock-in, and a clock-out once the shift ends. `receptionistName`
 * is copied in so the log still reads correctly if the roster entry is renamed
 * or retired. `workDate` is the club-local day the shift is counted against.
 */
export interface AttendanceRecord {
  id: string;
  clubId: string;
  receptionistId: string;
  receptionistName: string;
  workDate: string; // 'YYYY-MM-DD' in the club timezone
  clockInAt: ISODateTime;
  clockOutAt: ISODateTime | null;
}

// ---------------------------------------------------------------------------
// Stations
// ---------------------------------------------------------------------------

export type StationStatus = 'AVAILABLE' | 'IN_USE' | 'PAUSED' | 'MAINTENANCE';

export interface Station {
  id: string;
  clubId: string;
  name: string;
  type: StationType;
  status: StationStatus;
  /** Optional GPIO pin for the Pi light service. Never hardcoded in UI. */
  gpioPin: number | null;
  sortOrder: number;
}

// ---------------------------------------------------------------------------
// Rates (time-of-day windows)
// ---------------------------------------------------------------------------

export interface RateWindow {
  id: string;
  clubId: string;
  stationType: StationType;
  label: string;
  /** Minutes since local midnight, [start, end). Overnight allowed via end < start. */
  startMinute: number;
  endMinute: number;
  ratePerMinutePaise: Paise;
  /** 0=Sun … 6=Sat. Empty = every day. */
  daysOfWeek: number[];
  active: boolean;
}

// ---------------------------------------------------------------------------
// Sessions & billing
// ---------------------------------------------------------------------------

export type SessionStatus = 'ACTIVE' | 'PAUSED' | 'COMPLETED';

/**
 * How a bill was settled:
 * - CASH / UPI  — paid in full by that method
 * - PENDING     — khata; recorded as a due, settled later
 * - MIX         — split across cash + online (see Payment.mix)
 * - MEMBERSHIP  — deducted from a member's wallet via an RFID card tap
 */
export type PaymentMethod = 'CASH' | 'UPI' | 'PENDING' | 'MIX' | 'MEMBERSHIP' | 'COMP';

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  PENDING: 'Pending (Khata)',
  MIX: 'Mix',
  MEMBERSHIP: 'Membership',
  COMP: 'Cleared (Admin)',
};

export interface Session {
  id: string;
  clubId: string;
  stationId: string;
  status: SessionStatus;
  startedAt: ISODateTime;
  endedAt: ISODateTime | null;
  /** Rate captured at start so mid-session rate changes don't rewrite history. */
  ratePerMinutePaise: Paise;
  /** Accumulated paused milliseconds (billable time excludes these). */
  pausedMs: number;
  /** When currently paused, the instant the pause began; else null. */
  pausedAt: ISODateTime | null;
  customerName: string | null;
  playerCount: number;
  note: string | null;
}

/** A fully computed bill for a session — the output of lib/billing.ts. */
export interface Bill {
  sessionId: string;
  billableMs: number;
  billedMinutes: number;
  ratePerMinutePaise: Paise;
  tableAmountPaise: Paise;
  foodAmountPaise: Paise;
  /** Extra/buffer charge added on top (e.g. a round-up or manual add). */
  extraPaise: Paise;
  discountPaise: Paise;
  subtotalPaise: Paise;
  totalPaise: Paise;
}

export interface PaymentSplit {
  cashPaise: Paise;
  onlinePaise: Paise;
}

export interface Payment {
  id: string;
  clubId: string;
  sessionId: string | null;
  bookingId: string | null;
  customerName: string | null;
  /** Set when method is MEMBERSHIP — the member whose wallet was charged. */
  memberId: string | null;
  amountPaise: Paise;
  method: PaymentMethod;
  /** Set when method is MIX — how the total split across cash + online. */
  mix: PaymentSplit | null;
  status: 'PAID' | 'PENDING';
  createdAt: ISODateTime;
}

// ---------------------------------------------------------------------------
// Food & bar
// ---------------------------------------------------------------------------

export type MenuCategory = 'FOOD' | 'BEVERAGE' | 'BAR' | 'SNACK';

export interface MenuItem {
  id: string;
  clubId: string;
  name: string;
  category: MenuCategory;
  pricePaise: Paise;
  available: boolean;
  emoji: string | null;
}

export interface FoodOrderLine {
  menuItemId: string;
  name: string;
  unitPricePaise: Paise;
  quantity: number;
}

export interface FoodOrder {
  id: string;
  clubId: string;
  /** Attached to a running session, or null for a standalone counter sale. */
  sessionId: string | null;
  lines: FoodOrderLine[];
  status: 'OPEN' | 'COMPLETED' | 'CANCELLED';
  createdAt: ISODateTime;
}

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------

export type BookingStatus = 'PENDING' | 'CONFIRMED' | 'SEATED' | 'CANCELLED';

export interface Booking {
  id: string;
  clubId: string;
  stationId: string | null;
  stationType: StationType | null;
  customerName: string;
  phone: string | null;
  partySize: number;
  startAt: ISODateTime;
  durationMinutes: number;
  status: BookingStatus;
  createdAt: ISODateTime;
  /** Operator id that created it, or null when it came from the public site. */
  createdBy: string | null;
}

// ---------------------------------------------------------------------------
// Customers, expenses, activity
// ---------------------------------------------------------------------------

export interface Customer {
  id: string;
  clubId: string;
  name: string;
  phone: string | null;
  totalSpendPaise: Paise;
  outstandingPaise: Paise;
  lastVisit: ISODateTime | null;
}

export interface Expense {
  id: string;
  clubId: string;
  category: string;
  amountPaise: Paise;
  note: string | null;
  incurredAt: ISODateTime;
}

export type ActivityType =
  | 'PAYMENT'
  | 'SESSION_START'
  | 'SESSION_END'
  | 'BOOKING'
  | 'FOOD_ORDER'
  | 'LIGHT';

export interface ActivityEvent {
  id: string;
  clubId: string;
  type: ActivityType;
  message: string;
  amountPaise: Paise | null;
  at: ISODateTime;
}

// ---------------------------------------------------------------------------
// Pi lighting
// ---------------------------------------------------------------------------

export interface LightState {
  stationId: string;
  gpioPin: number | null;
  on: boolean;
  /** Last time the Pi acknowledged a command for this station. */
  updatedAt: ISODateTime | null;
}

export interface PiStatus {
  online: boolean;
  mode: 'mock' | 'live';
  lastSeen: ISODateTime | null;
  lights: LightState[];
}

// ---------------------------------------------------------------------------
// Membership, wallet & RFID cards
// ---------------------------------------------------------------------------

export type MemberStatus = 'ACTIVE' | 'INACTIVE';

/**
 * One RFID tag bound to a member. A member may have several (e.g. a card plus a
 * keyfob) — any of them resolves to the same wallet. The UID is stored uppercased
 * and colon-free (e.g. "04A1B2C3") and is only an identifier; the balance is
 * server-authoritative, never on the tag.
 */
export type CardRole = 'MEMBER' | 'ADMIN';

export interface MemberCard {
  uid: string;
  label: string;
  /**
   * ADMIN tags are owner/manager overrides: tapping one at billing CLEARS the
   * bill (comps it) and charges nothing — no wallet is debited.
   */
  role: CardRole;
  addedAt: ISODateTime;
}

export interface Member {
  id: string;
  clubId: string;
  name: string;
  phone: string | null;
  /** All RFID tags linked to this member. Empty until one is enrolled. */
  cards: MemberCard[];
  walletBalancePaise: Paise;
  status: MemberStatus;
  planId: string | null;
  joinedAt: ISODateTime;
}

export type WalletTxnType = 'TOPUP' | 'DEBIT' | 'REFUND' | 'ADJUSTMENT';

export interface WalletTransaction {
  id: string;
  clubId: string;
  memberId: string;
  type: WalletTxnType;
  /** Always positive; `type` gives the direction. */
  amountPaise: Paise;
  balanceAfterPaise: Paise;
  note: string | null;
  /** Links a DEBIT back to the session/payment it settled, when applicable. */
  sessionId: string | null;
  paymentId: string | null;
  createdAt: ISODateTime;
}

/** Membership plans — the paid tiers. Shipping "coming soon" for now. */
export interface MembershipPlan {
  id: string;
  clubId: string;
  name: string;
  pricePaise: Paise;
  billingPeriod: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  benefits: string[];
  status: 'COMING_SOON' | 'ACTIVE';
}

/** A single RFID card tap surfaced by the reader (ESP32 + RC522). */
export interface CardTap {
  uid: string;
  at: ISODateTime;
  readerId: string | null;
}

export interface ReaderStatus {
  online: boolean;
  mode: 'mock' | 'live';
  readerId: string | null;
  lastTapAt: ISODateTime | null;
}
